import {
  useCallback,
  useState,
  type Dispatch,
  type SetStateAction
} from 'react';

import useTestTabBrowseCards from '@api/hooks/goal/test-tab/useTestTabBrowseCards';
import type { BrowseSection } from '@api/fetchers/goal/test-tab/types';
import { TEST_SERIES_FILTER } from '../Common/TestSeriesFilters';
import { TEST_SERIES_TAB } from '../Common/TestSeriesTabSwitcher';
import {
  buildTestTabBaseProps,
  saveTestTabAnalyticsContext,
  trackTestTestsSubSectionClicked
} from './testTabAnalytics';
import { getCategoryTabIdFromBrowseSectionId } from './browseSections';
import { getDefaultCategoryTab } from '../Common/TestSeriesContentPanel';
import {
  API_SUBSCRIPTION,
  UI_SUBSCRIPTION,
  UI_TO_API_SUBSCRIPTION
} from './constants';
import { createEmptyFilterSelections } from './parseFilterMetadata';
import type { FilterSelections } from './types';

const ALL_UI_SUBSCRIPTIONS = [
  UI_SUBSCRIPTION.PRIME,
  UI_SUBSCRIPTION.PLUS,
  UI_SUBSCRIPTION.ICONIC
];

const getBrowseSubscriptionParam = (subscription: string[] = []) => {
  const apiIds = [
    ...new Set(
      subscription.map((id) => UI_TO_API_SUBSCRIPTION[id]).filter(Boolean)
    )
  ];

  return apiIds.length > 0 ? apiIds.join(',') : API_SUBSCRIPTION.FREE;
};

interface UseTestTabV2BrowseParams {
  goalUid?: string;
  goalDisplayName: string;
  language?: number;
  subscription?: string[];
  isPlusOrIconicUser?: boolean;
  enabled: boolean;
  activeTestTab: string;
  setActiveCategoryTab: (categoryTabId: string) => void;
  setAppliedFilters: Dispatch<SetStateAction<FilterSelections>>;
}

export const getTestTabV2ResetFilters = (
  isPlusOrIconicUser = false
): FilterSelections => {
  const filters = createEmptyFilterSelections();
  filters.subscription = isPlusOrIconicUser
    ? ALL_UI_SUBSCRIPTIONS
    : [UI_SUBSCRIPTION.PRIME];
  return filters;
};

export const getListingFilterConfig = (
  filterConfig: Array<{ id: string }>,
  isTestTabV2: boolean
) =>
  isTestTabV2
    ? filterConfig.filter((item) => item.id !== TEST_SERIES_FILTER.SUBSCRIPTION)
    : filterConfig;

const useTestTabV2Browse = ({
  goalUid,
  goalDisplayName,
  language,
  subscription,
  isPlusOrIconicUser = false,
  enabled,
  activeTestTab,
  setActiveCategoryTab,
  setAppliedFilters
}: UseTestTabV2BrowseParams) => {
  const [selectedBrowseSectionId, setSelectedBrowseSectionId] = useState<
    string | null
  >(null);

  const isBrowseLanding =
    enabled &&
    activeTestTab === TEST_SERIES_TAB.ALL &&
    !selectedBrowseSectionId;

  const { sections: browseSections, isLoading: isBrowseLoading } =
    useTestTabBrowseCards({
      goalUid,
      language,
      subscription: getBrowseSubscriptionParam(subscription),
      enabled: enabled && activeTestTab === TEST_SERIES_TAB.ALL
    });

  const resetBrowseSelection = useCallback(() => {
    setSelectedBrowseSectionId(null);
  }, []);

  const handleBrowseSectionSelect = useCallback(
    (section: BrowseSection) => {
      const nextCategoryTab = getCategoryTabIdFromBrowseSectionId(section.id);

      if (!nextCategoryTab) {
        return;
      }

      const baseProps = buildTestTabBaseProps({
        view: TEST_SERIES_TAB.ALL,
        categoryTabId: nextCategoryTab,
        appliedSelections: getTestTabV2ResetFilters(isPlusOrIconicUser),
        isTestTabV2: true
      });

      trackTestTestsSubSectionClicked({
        goalUid,
        goalName: goalDisplayName,
        ...baseProps
      });
      saveTestTabAnalyticsContext(baseProps);

      setSelectedBrowseSectionId(section.id);
      setActiveCategoryTab(nextCategoryTab);
      setAppliedFilters((prev) => ({
        ...getTestTabV2ResetFilters(isPlusOrIconicUser),
        subscription: prev.subscription
      }));
    },
    [
      goalDisplayName,
      goalUid,
      isPlusOrIconicUser,
      setActiveCategoryTab,
      setAppliedFilters
    ]
  );

  const handleBrowseClose = useCallback(() => {
    setSelectedBrowseSectionId(null);
    setActiveCategoryTab(getDefaultCategoryTab(TEST_SERIES_TAB.ALL));
  }, [setActiveCategoryTab]);

  return {
    selectedBrowseSectionId,
    isBrowseLanding,
    browseSections,
    isBrowseLoading,
    resetBrowseSelection,
    handleBrowseSectionSelect,
    handleBrowseClose
  };
};

export default useTestTabV2Browse;
